// This is intended to eventually replace components/ExplainSidebar

import React, { useState } from "react";

import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";

import { describeAnnotation, getAnnotationTitle } from "utils/explain";
import {
  useAnnotationLink,
  useExplainSelection,
  useSelectedAnnotationId,
  useSelectedNode,
} from "features/Explain/WithExplainNodeSelection";
import { Annotation } from "types/explain";
import PanelSection from "ui/PanelSection";
import ExplainStatsAndOptions from "features/Explain/ExplainStatsAndOptions";
import NodeIdBadge from "features/Explain/ExplainNodeTree/NodeIdBadge";
import {
  ComparablePlanType,
  useCurrentPlan,
} from "features/Explain/WithExplainPlan";

import { ExpandToggle } from "ui/Icons";
import Button from "ui/Button";
import Popover from "ui/Popover";
import { Link } from "react-router-dom";
import { useRoutes } from "utils/routes";

import { useExplainInsightsWorkbookSupportQuery } from "./gql/Query.generated";

export default function ExplainSidebarV2({
  databaseId,
  queryId,
  issueId,
  relatedWorkbookId,
  plan,
  blockSize,
  ioBlocks,
}: {
  databaseId: string;
  queryId: string;
  issueId?: string;
  relatedWorkbookId: string;
  plan: ComparablePlanType;
  ioBlocks: number;
  blockSize: number;
}) {
  const { databaseQueryExplain } = useRoutes();

  return (
    <>
      <ExplainStatsAndOptions
        className="mb-2"
        seenAt={plan.seenAt}
        fingerprint={plan.fingerprint}
        totCost={plan.totCost}
        ioBlocks={ioBlocks}
        ioMs={plan.ioMs}
        plan={plan.plan.plan}
        runtime={plan.runtime}
        blockSize={blockSize}
        planLink={
          plan.humanId &&
          databaseQueryExplain(databaseId, queryId, plan.humanId)
        }
      />
      <PanelSection className="-mx-2">
        <ExplainInsights
          databaseId={databaseId}
          queryId={queryId}
          plan={plan}
          issueId={issueId}
          relatedWorkbookId={relatedWorkbookId}
        />
      </PanelSection>
    </>
  );
}

export function ExplainInsights({
  databaseId,
  queryId,
  plan,
  relatedWorkbookId,
  issueId,
}: {
  databaseId: string;
  queryId: string;
  plan: ComparablePlanType;
  relatedWorkbookId: string;
  issueId?: string;
}) {
  const { qaAnnotations, otherAnnotations } = groupBy(
    plan.plan.annotations,
    (ann) => {
      return ann.type.startsWith("query advisor/")
        ? "qaAnnotations"
        : "otherAnnotations";
    },
  );

  const othersByType = groupBy(otherAnnotations, (ann) => ann.type);

  const selectedAnnotationId = useSelectedAnnotationId();
  const hasQueryAdvisorAnnotations = qaAnnotations?.length > 0;
  const hasAnyAnnotations = plan.plan.annotations.length > 0;

  // Query Advisor reports insights for write queries too, but workbooks only
  // support SELECT, so we need this to avoid offering a workbook we can't create
  const { data: workbookSupport } = useExplainInsightsWorkbookSupportQuery({
    variables: { databaseId, queryId },
    skip: !hasQueryAdvisorAnnotations || !!relatedWorkbookId,
  });
  const supportsWorkbooks = workbookSupport?.getQueryDetails.supportsWorkbooks;

  return (
    <>
      <h4 className="text-[#606060]">Insights</h4>
      {/*
          If we have any insights for this plan at all, highlight Query Advisor
          insights explicitly, whether we have them or not. If we have no
          annotations at all, just say we've got nothing, without mentioning
          Query Advisor.
        */}
      {hasAnyAnnotations ? (
        <>
          <h5 className="mb-1 font-semibold text-[#333333]">Query Advisor</h5>
          {hasQueryAdvisorAnnotations ? (
            <>
              <div className="flex flex-col gap-2">
                {qaAnnotations.map((annotation, i) => {
                  return (
                    <AnnotationDisplay
                      databaseId={databaseId}
                      queryId={queryId}
                      issueId={issueId}
                      relatedWorkbookId={relatedWorkbookId}
                      supportsWorkbooks={supportsWorkbooks}
                      key={i}
                      annotation={annotation}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div>No Query Advisor insights found for this plan.</div>
          )}
          {Object.entries(othersByType).map(([type, annotations]) => {
            const defaultExpand =
              !hasQueryAdvisorAnnotations ||
              annotations.some((ann) => ann.id === selectedAnnotationId);

            return (
              <HeuristicInsights
                key={type}
                type={type}
                annotations={annotations}
                defaultExpanded={defaultExpand}
              />
            );
          })}
        </>
      ) : (
        <div>No automated insights found for this plan.</div>
      )}
    </>
  );
}

function HeuristicInsights({
  type,
  annotations,
  defaultExpanded = false,
}: {
  type: string;
  annotations: Annotation[];
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  function handleToggleExpanded() {
    setExpanded((exp) => !exp);
  }

  // If we have multiple annotations of the same type for the same node, this
  // will omit some of them, but that should not happen
  const annotationsByNode = annotations.reduce<Map<number, Annotation>>(
    (map, ann) => {
      ann.nodes.forEach((node) => {
        map.set(node.id, ann);
      });
      return map;
    },
    new Map(),
  );
  const sortedByNode = sortBy(
    Array.from(annotationsByNode.entries()),
    ([nodeId, _anns]) => nodeId,
  );

  return (
    <>
      <Button className="w-full" bare onClick={handleToggleExpanded}>
        <h5 className="mb-1 flex justify-between font-semibold text-[#333333]">
          {getAnnotationTitle(type)}
          <ExpandToggle expanded={expanded} />
        </h5>
      </Button>
      {expanded && (
        <ul className="flex flex-col !ml-0">
          {sortedByNode.map(([nodeId, annotation]) => {
            return (
              <li key={nodeId} className="list-none">
                <LinkedAnnotation nodeId={nodeId} annotationId={annotation.id}>
                  {annotation.detail}
                </LinkedAnnotation>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function LinkedAnnotation({
  nodeId,
  annotationId,
  children,
}: {
  nodeId: number;
  annotationId: number;
  children: React.ReactNode;
}) {
  const AnnotationLink = useAnnotationLink();

  const [selectedNode] = useSelectedNode();
  const selectedAnnotationId = useSelectedAnnotationId();
  const selection = useExplainSelection();

  const ofSelectedNode =
    selectedNode && selection === "node" && selectedNode?.extra.id === nodeId;

  const isHighlighted = annotationId === selectedAnnotationId || ofSelectedNode;
  const isSelected =
    selection === "annotation" && annotationId === selectedAnnotationId;

  return (
    // AnnotationLink is a memoized component injected via SelectedNodeContext, not
    // created during this render; it holds no state, so re-mounting is harmless.
    // eslint-disable-next-line react-hooks/static-components
    <AnnotationLink
      annotationId={isSelected ? undefined : annotationId}
      className="flex items-start gap-1"
    >
      <NodeIdBadge nodeId={nodeId} highlighted={isHighlighted} /> {children}
    </AnnotationLink>
  );
}

function AnnotationDisplay({
  annotation,
  databaseId,
  queryId,
  issueId,
  relatedWorkbookId,
  supportsWorkbooks,
}: {
  annotation: Annotation;
  databaseId?: string;
  queryId?: string;
  issueId?: string;
  relatedWorkbookId?: string;
  supportsWorkbooks?: boolean;
}) {
  const { databaseWorkbook, databaseWorkbooksNew } = useRoutes();

  const plan = useCurrentPlan();
  const description = describeAnnotation(annotation, plan.plan.plan);
  const nodeId = annotation.nodes.at(0)?.id;

  return (
    <div>
      <LinkedAnnotation nodeId={nodeId} annotationId={annotation.id}>
        {description.title}
      </LinkedAnnotation>
      <div className="!ml-[22px] !my-2">
        <ul className="!ml-[16px]">
          {description.details.map((detailLine, i) => {
            return <li key={i}>{detailLine}</li>;
          })}
        </ul>
        {annotation.type.startsWith("query advisor") &&
          (relatedWorkbookId ? (
            <Link to={databaseWorkbook(databaseId, relatedWorkbookId)}>
              View workbook
            </Link>
          ) : supportsWorkbooks === false ? (
            <Popover
              content={<div>Workbooks only support SELECT queries.</div>}
            >
              <span className="btn btn-primary disabled">Create Workbook</span>
            </Popover>
          ) : (
            <Link
              className="btn btn-primary"
              to={databaseWorkbooksNew(databaseId, queryId, issueId, plan.id)}
            >
              Create Workbook
            </Link>
          ))}
      </div>
    </div>
  );
}
